import { useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { Button, Checkbox, Segment, Select, Table } from "semantic-ui-react";
import { toast } from "../../../../../..";
import {
    AddAdditionalOutage,
    SetAOCountry,
    SetAOLort,
    SetAOVoltage,
} from "../../../../../../actions/additionalOutageActions";
import { countryOptions, lortOptions, voltageOptions } from "../../../../../../models/nec";
import { IAdditionalOutage } from "../../../../../../store/additionalOutageReducer";
import { IBranch } from "../../../../../../store/cgmBranchesReducer";
import { AppState } from "../../../../../../store/configureStore";
import { Loading } from "../../../../../Loading/Loading";
import { extractLinesCGM } from "../../../zipLinesHelper";
import { sortReducer } from "../sortReducer";
import { ITimestamp } from "../../../../../TimestampsSelector/timestamps";

export const AdditionalOutage = () => {
    const dispatch = useDispatch();
    const zipFile = useSelector((state: AppState) => state.files.zipFile);
    const activeTimestamps = useSelector((state: AppState) => state.timestamps.timestamps);
    const { additionalOutages, country, lort, voltage } = useSelector(
        (state: AppState) => state.additionalOutage
    );
    const [loading, setLoading] = useState(false);
    const [lines, setLines] = useState<IBranch[]>([]);
    const [selectedLine, setSelectedLine] = useState<IBranch>();
    const [selectedRow, setSelectedRow] = useState("");
    const [state, dispatchLocal] = useReducer(sortReducer, {
        column: null,
        data: lines,
        direction: null,
    });
    const { column, data, direction } = state;
    const [selectedTimestamps, setSelectedTimestamps] = useState<ITimestamp[]>([]);

    const handleSetSelectedTimestamps = (timestamp: ITimestamp) => {
        selectedTimestamps.includes(timestamp)
            ? setSelectedTimestamps(
                  selectedTimestamps.filter((x: ITimestamp) => x !== timestamp)
                  .sort((a, b) => a.key - b.key),
                  //.sort((a, b) => a - b)
              )
            : setSelectedTimestamps(selectedTimestamps.concat(timestamp)
              .sort((a, b) => a.key - b.key));
            //.sort((a, b) => a - b));
    };

    const handleToggleSelectAllTimestamps = () => {
        selectedTimestamps.length < activeTimestamps.length
            ? setSelectedTimestamps(activeTimestamps)
            : setSelectedTimestamps([]);
    };

    const checkAddAdditionalOutage = (branch: IBranch) => {
        if (additionalOutages.some(x => x.branch.key === branch.key))
            toast("Additional outage already added", false);
        else if (selectedTimestamps.length === 0)
            toast("Please select at least one timestamp", false);
        else {
            const newAO: IAdditionalOutage = {
                branch: branch,
                timestamps: selectedTimestamps.map(d => d.key),
            };
            dispatch(AddAdditionalOutage(newAO));
            setSelectedTimestamps([]);
            setSelectedRow("");
            setSelectedLine(undefined);
            toast("Additional outage added", true);
        }
    };

    const handleSelectLine = (line: IBranch, key: string) => {
        setSelectedLine({ ...line, key: key });
        setSelectedRow(key);
    };

    useEffect(() => {
        dispatchLocal({ type: "SET_DATA", data: lines });
    }, [lines]);

    useEffect(() => {
        if (zipFile && country && activeTimestamps.length > 0 && lort) {
            setLoading(true);
            extractLinesCGM(zipFile, country, activeTimestamps, lort, voltage).then(newLines => {
                setLines(newLines);
                setLoading(false);
            });
        }
    }, [zipFile, country, lort, voltage, activeTimestamps]);

    return (
        <div id="div-cc-validation-outage-a" className="cc-validation-outage transition-container">
            <div id="selectors-lvl-1-additionalOutage" className="selectors-lvl-1">
                <div id="cc-validation-selector-wrapper-additionalOutage" className="cc-validation-selector-wrapper">
                    <p id="p-country-additionalOutage">Country</p>
                    <Select
                        id="select-additionalOutage-a"
                        search
                        selectOnBlur={false}
                        upward={false}
                        className="country-select"
                        options={countryOptions}
                        onChange={(e, data: any) => dispatch(SetAOCountry(data.value))}
                        value={country}
                    />
                </div>
                <div id="div-additionalOutage-a" className="cc-validation-selector-wrapper">
                    <p id="p-lines/trasnformers-additionalOutage-a">Lines/transformers</p>
                    <Select
                        id="select-additionalOutage-b"
                        selectOnBlur={false}
                        upward={false}
                        className="lort-select"
                        options={lortOptions}
                        onChange={(e, data: any) => dispatch(SetAOLort(data.value))}
                        value={lort}
                    />
                </div>
                <div id="div-additionalOutage-b" className="cc-validation-selector-wrapper">
                    <p id="p-voltageLevel-additionalOutage-a">Voltage level</p>
                    <Select
                        id="select-additionalOutage-c"
                        clearable
                        selectOnBlur={false}
                        upward={false}
                        className="voltage-select"
                        options={voltageOptions}
                        onChange={(e, data: any) => dispatch(SetAOVoltage(data.value))}
                        value={voltage}
                    />
                </div>
            </div>
            {loading ? (
                <Loading />
            ) : country && zipFile && lort && activeTimestamps.length > 0 ? (
                <div id="div-additionalOutage-c" className="cc-validation-outage-container">
                    <Table id="table-additionalOutage-a" sortable selectable celled className="cgm-table">
                        <Table.Header id="tableHeader-additionalOutage-a">
                            <Table.Row id="tableRow-additionalOutage-a">
                                <Table.HeaderCell
                                    id="tableHeaderCell-additionalOutage-a"
                                    sorted={column === "timestamp" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "timestamp",
                                        })
                                    }
                                >
                                    Timestamp
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-additionalOutage-b"
                                    sorted={column === "node1" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "node1",
                                        })
                                    }
                                >
                                    Node 1
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-additionalOutage-c"
                                    sorted={column === "node2" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "node2",
                                        })
                                    }
                                >
                                    Node 2
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-additionalOutage-d"
                                    sorted={column === "orderCode" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "orderCode",
                                        })
                                    }
                                >
                                    Order
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-additionalOutage-e"
                                    sorted={column === "elementName" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "elementName",
                                        })
                                    }
                                >
                                    Element name
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        {data.length !== 0 &&
                            data.map((l: IBranch) => (
                                <Table.Body
                                    id={`tableBody-additionalOutage-a-${l.name}`}
                                    key={`${l.timestamp}${l.node1}${l.node2}${l.orderCode}${l.elementName}`}
                                >
                                    <Table.Row
                                        id={`tableRow-additionalOutage-b-${l.name}`}
                                        className={`${
                                            selectedRow ===
                                            `${l.node1}${l.node2}${l.orderCode}${l.elementName}`
                                                ? "selected-row"
                                                : ""
                                        }`}
                                        key={`${l.node1}${l.node2}${l.orderCode}${l.elementName}`}
                                        onClick={() =>
                                            handleSelectLine(
                                                l,
                                                `${l.node1}${l.node2}${l.orderCode}${l.elementName}`
                                            )
                                        }
                                    >
                                        <Table.Cell id={`tableCell-additionalOutage-a-${l.name}`}>{l.timestamp}</Table.Cell>
                                        <Table.Cell id={`tableCell-additionalOutage-b-${l.name}`}>{l.node1}</Table.Cell>
                                        <Table.Cell id={`tableCell-additionalOutage-c-${l.name}`}>{l.node2}</Table.Cell>
                                        <Table.Cell id={`tableCell-additionalOutage-d-${l.name}`}>{l.orderCode}</Table.Cell>
                                        <Table.Cell id={`tableCell-additionalOutage-e-${l.name}`}>{l.elementName}</Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            ))}
                    </Table>
                    <CSSTransition
                        id="cssTransition-additionalOutage-a"
                        in={lines.length > 0 && selectedLine !== undefined}
                        timeout={400}
                        classNames="neselector-transition"
                        unmountOnExit
                    >
                        <div id="cc-validation-actions-message">
                            <Segment id="segment-additionalOutage-a">
                                {activeTimestamps.map((timestamp, index) => (
                                    <Checkbox
                                        id={`checkbox-additionalOutage-a-${index}`}
                                        //key={timestamp}
                                        key={timestamp.key}
                                        className="timestamp-checkbox"
                                        checked={selectedTimestamps.includes(timestamp)}
                                        //label={timestamp.toString().padStart(2, "0") + ":30"}
                                        label={timestamp.text}
                                        onClick={() => handleSetSelectedTimestamps(timestamp)}
                                    />
                                ))}
                                <Checkbox
                                    id="checkbox-additionalOutage-b"
                                    className="timestamp-checkbox"
                                    checked={selectedTimestamps.length === activeTimestamps.length}
                                    label={
                                        selectedTimestamps.length < activeTimestamps.length
                                            ? "Select all"
                                            : "Deselect all"
                                    }
                                    onClick={() => handleToggleSelectAllTimestamps()}
                                />
                            </Segment>
                            <Button
                                id="button-additionalOutage-b"
                                primary
                                content="Add outage"
                                disabled={!selectedRow || selectedTimestamps.length === 0}
                                onClick={() => checkAddAdditionalOutage(selectedLine!)}
                            />
                        </div>
                    </CSSTransition>
                </div>
            ) : !zipFile ? (
                <p id="p-additionalOutage-uploadFile">Please upload a file</p>
            ) : !country ? (
                <p id="p-additionalOutage-selectCountry"> Please select country</p>
            ) : !lort ? (
                <p id="p-additionalOutage-selectLines"> Please select lines or transformers</p>
            ) : activeTimestamps.length === 0 ? (
                <p id="p-additionalOutage-selectTimestamp"> Please select at least one timestamp</p>
            ) : null}
        </div>
    );
};
